<template>
  <v-dialog v-model="dialog" width="1000" :persistent="true">
    <template v-slot:activator="{ on }">
      <permission-button
          :permission="canCreate"
          color="primary"
          :tooltip="{top: true, bottom: false, attach: false}"
          @click="open"
          class="ml-0 mr-0"
      >
        {{ 'app.add_product' | trans }}
      </permission-button>
    </template>

    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>{{ modalTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text class="pt-0">
        <v-form lazy-validation v-model="stepsValidity.zeroStep" ref="step0Form">
          <v-layout row>
            <v-flex xs6>
              <v-text-field
                  v-model="product.name"
                  :label="`app.name` | trans"
                  single-line
                  class="pr-1"
                  :rules="rules.name"
              ></v-text-field>
            </v-flex>
            <v-flex xs6>
              <v-autocomplete
                  item-text="name"
                  item-value="id"
                  clearable
                  v-model="product.category"
                  :items="categories"
                  :label="'app.pages.products.category' | trans"
                  :rules="rules.category"
              >
                <template slot="item" slot-scope="data">
                  <p :class="data.item.is_main && 'isMain'">{{ data.item.name }}</p>
                </template>
              </v-autocomplete>
            </v-flex>
          </v-layout>
        </v-form>

        <v-layout row wrap v-if="hasPermission('product_system_create')">
          <v-flex xs12>
            <v-checkbox color="primary" v-model="product.is_system" :label="'app.products.create_modal.system_product' | trans"></v-checkbox>
          </v-flex>
        </v-layout>

        <v-layout row>
          <v-flex xs12 mt-2>
            <v-stepper v-model="step">
              <v-stepper-header class="v-toolbar v-toolbar--card theme--dark primary">
                <v-stepper-step :complete="step > 1" step="1" class="cursor-pointer" @click="goToStep(1)">
                  {{ 'app.menu.basic' | trans }}
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="step > 2" step="2" class="cursor-pointer" @click="goToStep(2)">
                  {{ 'app.minerals' | trans }}
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="step > 3" step="3" class="cursor-pointer" @click="goToStep(3)">
                  {{ 'app.vitamins' | trans }}
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="step > 4" step="4" class="cursor-pointer" @click="goToStep(4)">
                  {{ 'app.amino_acids' | trans }}
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="step > 5" step="5" class="cursor-pointer" @click="goToStep(5)">
                  {{ 'app.pages.products_weights' | trans }}
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step step="6" class="cursor-pointer" @click="goToStep(6)">
                  {{ 'app.pages.products.seasonality_and_tags' | trans }}
                </v-stepper-step>
              </v-stepper-header>

              <v-stepper-content step="1" class="pa-1">
                <v-layout row>
                  <v-flex class="mb-2 mr-2 text-right">
                    {{ 'app.products.create_modal.info_alert' | trans }}
                  </v-flex>
                </v-layout>
                <v-layout row>
                  <v-flex xs12 d-flex>
                    <v-form lazy-validation v-model="stepsValidity.firstStep" ref="step1Form">
                      <v-layout row wrap justify-start>
                        <v-flex xs2 my-2 d-flex v-for="nutrient in initialData[1]" :key="nutrient.id">
                          <v-text-field v-if="nutrient.id === CARBO_ID"
                                        text-xs-right
                                        v-model="product.nutrients[nutrient.id]"
                                        @input="handleCarboChange()"
                                        full
                                        :suffix="nutrient.unit"
                                        class="text-field-right my-0 py-0 px-2"
                                        :label="nutrient.short_name"
                                        :hint="nutrient.name !== nutrient.short_name ? nutrient.name : undefined"
                                        :rules="requiredNutrientsIds.includes(nutrient.id) ? rules.requiredNutrient : (nutrient.id === FIBER_ID ? rules.fiber : rules.nutrient)"
                          ></v-text-field>
                          <v-text-field v-else
                                        text-xs-right
                                        v-model="product.nutrients[nutrient.id]"
                                        full
                                        @input="switchCommaToPeriod(nutrient.id)"
                                        :suffix="nutrient.unit"
                                        class="text-field-right my-0 py-0 px-2"
                                        :label="nutrient.short_name"
                                        :hint="nutrient.name !== nutrient.short_name ? nutrient.name : undefined"
                                        :rules="requiredNutrientsIds.includes(nutrient.id) ? rules.requiredNutrient : (nutrient.id === FIBER_ID ? rules.fiber : rules.nutrient)"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-form>
                  </v-flex>
                </v-layout>
                <v-layout row wrap justify-start>
                  <v-flex xs4 d-flex my-2>
                    <v-text-field text-xs-right
                                  full
                                  class="text-field-right my-0 py-0 px-2"
                                  :label="'app.products.create_modal.ww' | trans"
                                  :value="ww"
                                  readonly
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs4 d-flex my-2>
                    <v-text-field text-xs-right
                                  full
                                  class="text-field-right my-0 py-0 px-2"
                                  :label="'app.products.create_modal.wbt' | trans"
                                  :value="wbt"
                                  readonly
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs4 d-flex my-2>
                    <v-text-field text-xs-right
                                  full
                                  class="text-field-right my-0 py-0 px-2"
                                  :label="'app.products.create_modal.lg' | trans"
                                  :value="gl"
                                  readonly
                    ></v-text-field>
                  </v-flex>
                </v-layout>
              </v-stepper-content>

              <v-stepper-content step="2" class="pa-1">
                <v-layout row>
                  <v-flex class="mb-2 mr-2 text-right">
                    {{ 'app.products.create_modal.info_alert' | trans }}
                  </v-flex>
                </v-layout>
                <v-layout row>
                  <v-flex xs12 d-flex>
                    <v-form lazy-validation v-model="stepsValidity.secondStep" ref="step2Form">
                      <v-layout row wrap justify-start>
                        <v-flex xs4 my-2 d-flex v-for="nutrient in initialData[2]" :key="nutrient.id">
                          <v-text-field text-xs-right
                                        v-model="product.nutrients[nutrient.id]"
                                        full
                                        :suffix="nutrient.unit"
                                        class="text-field-right my-0 py-0 px-2"
                                        :label="nutrient.name"
                                        :rules="rules.nutrient"
                                        @input="switchCommaToPeriod(nutrient.id)"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-form>
                  </v-flex>
                </v-layout>
              </v-stepper-content>

              <v-stepper-content step="3" class="pa-1">
                <v-layout row>
                  <v-flex class="mb-2 mr-2 text-right">
                    {{ 'app.products.create_modal.info_alert' | trans }}
                  </v-flex>
                </v-layout>

                <v-layout row>
                  <v-flex xs12 d-flex>
                    <v-form lazy-validation v-model="stepsValidity.thirdStep" ref="step3Form">
                      <v-layout row wrap justify-start>
                        <v-flex xs3 my-2 d-flex v-for="nutrient in initialData[3].filter(n => !separatedNutrientsIds.includes(n.id))" :key="nutrient.id">
                          <v-text-field text-xs-right
                                        v-model="product.nutrients[nutrient.id]"
                                        full
                                        :suffix="nutrient.unit"
                                        class="text-field-right my-0 py-0 px-2"
                                        :label="nutrient.short_name"
                                        :rules="rules.nutrient"
                                        :hint="nutrient.name !== nutrient.short_name ? nutrient.name : undefined"
                                        @input="switchCommaToPeriod(nutrient.id)"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                      <v-divider></v-divider>
                      <v-layout row wrap justify-start>
                        <v-flex xs4 my-2 d-flex v-for="nutrient in initialData[3].filter(n => separatedNutrientsIds.includes(n.id))" :key="nutrient.id">
                          <v-text-field text-xs-right
                                        v-model="product.nutrients[nutrient.id]"
                                        full
                                        :suffix="nutrient.unit"
                                        class="text-field-right my-0 py-0 px-2"
                                        :label="nutrient.name"
                                        :rules="rules.nutrient"
                                        @input="switchCommaToPeriod(nutrient.id)"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-form>
                  </v-flex>
                </v-layout>
              </v-stepper-content>

              <v-stepper-content step="4" class="pa-1">
                <v-layout row>
                  <v-flex class="mb-2 mr-2 text-right">
                    {{ 'app.products.create_modal.info_alert' | trans }}
                  </v-flex>
                </v-layout>

                <v-layout row>
                  <v-flex xs12 d-flex>
                    <v-form lazy-validation v-model="stepsValidity.fourthStep" ref="step4Form">
                      <v-layout row wrap justify-start>
                        <v-flex xs3 my-2 -flex v-for="nutrient in initialData[4]" :key="nutrient.id">
                          <v-text-field text-xs-right
                                        v-model="product.nutrients[nutrient.id]"
                                        full
                                        :suffix="nutrient.unit"
                                        class="text-field-right my-0 py-0 px-2"
                                        :label="nutrient.name"
                                        :rules="rules.nutrient"
                                        @input="switchCommaToPeriod(nutrient.id)"
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                    </v-form>
                  </v-flex>
                </v-layout>
              </v-stepper-content>

              <v-stepper-content step="5" class="pa-1">
                <v-flex xs12 d-flex>
                  <v-layout align-space-around justify-space-between column fill-height>
                    <v-form lazy-validation v-model="stepsValidity.fifthStep" ref="step5Form" class="mt-2">
                      <v-layout row wrap>
                        <v-flex xs3 d-flex v-for="productWeight in initialData[5]" :key="productWeight.id">
                          <v-layout align-space-around justify-space-between column fill-height>
                            <v-flex d-flex>
                              <v-text-field text-xs-right
                                            v-model="product.weights[productWeight.id]"
                                            full
                                            class="text-field-right my-0 py-0 px-2"
                                            suffix="g"
                                            :label="productWeight.name"
                                            :rules="rules.nutrient"
                                            @input="switchCommaToPeriod(productWeight.id)"
                              ></v-text-field>
                            </v-flex>
                          </v-layout>
                        </v-flex>
                      </v-layout>
                    </v-form>
                  </v-layout>
                </v-flex>
              </v-stepper-content>

              <v-stepper-content step="6" class="pa-1">
                <v-layout row>
                  <v-flex xs12 d-flex>
                    <v-form lazy-validation v-model="stepsValidity.sixthStep" ref="step6Form" class="mt-2">
                      <v-layout row wrap>
                        <v-flex xs3 d-flex>
                          <v-checkbox
                            v-model="seasons.fullYear"
                            :label="'app.products.create_modal.whole_year' | trans"
                            @change="fullYearClicked"
                            color="success"
                            height="0"
                          ></v-checkbox>
                        </v-flex>
                      </v-layout>

                      <v-layout row wrap>
                        <v-flex xs3 d-flex>
                          <v-checkbox
                            v-model="seasons.q1"
                            :label="'app.products.create_modal.first_quarter' | trans"
                            @change="quarterClicked($event, 1)"
                            color="success"
                            height="0"
                          ></v-checkbox>
                        </v-flex>
                        <v-flex xs3 d-flex>
                          <v-checkbox
                            v-model="seasons.q2"
                            :label="'app.products.create_modal.second_quarter' | trans"
                            @change="quarterClicked($event, 2)"
                            color="success"
                            height="0"
                          ></v-checkbox>
                        </v-flex>
                        <v-flex xs3 d-flex>
                          <v-checkbox
                            v-model="seasons.q3"
                            :label="'app.products.create_modal.third_quarter' | trans"
                            @change="quarterClicked($event, 3)"
                            color="success"
                            height="0"
                          ></v-checkbox>
                        </v-flex>
                        <v-flex xs3 d-flex>
                          <v-checkbox
                            v-model="seasons.q4"
                            :label="'app.products.create_modal.fourth_quarter' | trans"
                            @change="quarterClicked($event, 4)"
                            color="success"
                            height="0"
                          ></v-checkbox>
                        </v-flex>
                      </v-layout>

                      <v-layout row wrap>
                        <v-flex xs3 d-flex v-for="month in getMonths()" :key="month.number">
                          <v-checkbox
                            v-model="product.months[month.number]"
                            :label="month.name"
                            @change="monthClicked($event, month.number)"
                            color="success"
                            class="shrink mr-2"
                            height="0"
                          ></v-checkbox>
                        </v-flex>
                      </v-layout>
                      <v-layout row>
                        <v-flex xs12>
                          <v-alert
                            :value="true"
                            type="error"
                            v-if="seasonsLack"
                          >
                            {{ 'app.products.create_modal.select_at_least_one_month' | trans }}
                          </v-alert>
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <v-flex xs12 d-flex mt-3>
                          <v-combobox
                              multiple
                              clearable
                              v-model="productsTags"
                              :search-input.sync="syncProducts"
                              :items="productsItems"
                              item-text="name"
                              :label="trans('app.tags.tags')"
                          >
                            <template v-slot:no-data>
                              <v-list-tile>
                                <v-list-tile-content>
                                  <v-list-tile-title>
                                                        <span v-if="productsItems.length > 0"
                                                              v-html="trans('app.no_tag', {type: 'product', tag: syncProducts})"></span>
                                    <span v-else
                                          v-html="trans('app.tag_collection_empty', {type: 'product', tag: syncProducts})"></span>
                                  </v-list-tile-title>
                                </v-list-tile-content>
                              </v-list-tile>
                            </template>

                          </v-combobox>
                        </v-flex>
                      </v-layout>
                    </v-form>
                  </v-flex>
                </v-layout>
              </v-stepper-content>
            </v-stepper>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs12 d-flex my-2>
            <v-layout align-center justify-end row fill-height>
              <v-btn @click="goToStep(step - 1)" :disabled="step < 2">{{ 'app.back' | trans }}</v-btn>
              <v-btn
                color="primary"
                @click="goToStep(step + 1)"
                :disabled="currentStepValidity === false || step === 6"
              >
                {{ 'app.go_next' | trans }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" flat @click="close">{{ 'app.close' | trans }}</v-btn>
              <permission-button
                      :color="`success`"
                      @click="save(false)"
                      :permission="canSave"
                      :disabled="saveButtonsDisabled"
                      :loading="buttons.save.loading"
              >
                  {{ 'app.save' | trans }}
              </permission-button>
              <permission-button
                      :color="`success`"
                      @click="save(true)"
                      :permission="canCreate"
                      :disabled="saveButtonsDisabled"
                      :loading="buttons.saveAndProceed.loading"
              >
                  {{ 'app.products.create_modal.save_and_add_another' | trans }}
              </permission-button>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {mapState, mapActions, mapGetters} from "vuex";
import {helpers} from "../../helpers";
import axios from "axios";
import RulesBuilder from "../../utils/rulesBuilder";
import {trans} from "../../app";
import ProductInitialDataService from "../../services/ProductInitialDataService";
import {glCalculation, wbtCalculation, wwCalculation} from "../../utils/additionalNutrientCalculators";
import {DIETITIAN_FREE, DIETITIAN_TEST} from "../../utils/roles";

const CARBO_ID = 3;
const FIBER_ID = 20;
const PROTEIN_ID = 1;
const FATS_ID = 2;
const GRAM_WEIGHT_ID = 18;

export default {
  data: function () {
    return {
      WW_ID: 153,
      WBT_ID: 154,
      GLYCEMIC_LOAD_ID: 157,
      GLYCEMIC_INDEX_ID: 156,
      FIBER_ID,
      CARBO_ID,
      rulesBuilder: new RulesBuilder(),
      requiredNutrientsIds: [1, 2, 3, 5],
      separatedNutrientsIds: [43],
      dialog: false,
      step: 1,
      seasonsLack: false,
      product: {
        name: '',
        category: null,
        nutrients: {},
        weights: {},
        months: {
          1: true,
          2: true,
          3: true,
          4: true,
          5: true,
          6: true,
          7: true,
          8: true,
          9: true,
          10: true,
          11: true,
          12: true,
        },
        is_system: false,
        tags: [],
      },
      editingProductId: null,
      stepsValidity: {
        zeroStep: true,
        firstStep: true,
        secondStep: true,
        thirdStep: true,
        fourthStep: true,
        fifthStep: true,
        sixthStep: true,
      },
      initialData: {1: [], 2: [], 3: [], 4:[], 5: []},
      seasons: {
        fullYear: true,
        q1: true,
        q2: true,
        q3: true,
        q4: true,
      },
      buttons: {
        save: {
          loading: false,
          disabled: false,
        },
        saveAndProceed: {
          loading: false,
          disabled: false,
        },
      },
      syncProducts: '',
    };
  },
  async created() {
    const initialDataService = new ProductInitialDataService();
    const {data} = await initialDataService.fetch();
    this.initialData = {...data};
    this.initialData[1] = this.initialData[1].map(nutrient => {
      if (nutrient.id === CARBO_ID) {
        nutrient.name = 'Węglowodany ogółem';
      }

      return nutrient;
    });
  },
  methods: {
    ...mapActions(['FETCH_DICTIONARIES', 'fetchOwnProductsCount',]),
    open() {
      this.dialog = true;
    },
    async editProduct(productId) {
      const {data} = await axios.get(`/v2/products/${productId}`);
      const product = data.data;

      this.editingProductId = product.id;
      this.product.name = product.name;
      this.product.category = product.category.id;
      this.product.is_system = product.is_system;
      product.nutrients.forEach(nutrient => {
        //needs to be done this way, otherwise vue is not able to watch changes and calculate ww, wbt and łg
        const nutrientObject = {};
        nutrientObject[nutrient.id] = nutrient.value;
        this.product.nutrients = {...this.product.nutrients, ...nutrientObject}
      });
      product.weights
          .filter(weight => weight.id !== GRAM_WEIGHT_ID)
          .forEach(weight => {
            this.product.weights[weight.id] = weight.weight;
          });
      Object.keys(this.product.months).forEach(month => {
        if (product.seasons.includes(month) === false) {
          this.product.months[month] = false;
          this.monthClicked(false, month);
        }
      });
      this.product.tags = product.tags;
      this.open();
    },
    async copyAndEditProduct(productId) {
      const {data} = await axios.get(`/v2/products/${productId}`);
      const product = data.data;

      this.product.name = `${product.name} - ${trans('app.products.create_modal.copy')}`;
      this.product.category = product.category.id;
      product.nutrients.forEach(nutrient => {
        //needs to be done this way, otherwise vue is not able to watch changes and calculate ww, wbt and łg
        const nutrientObject = {};
        nutrientObject[nutrient.id] = nutrient.value;
        this.product.nutrients = {...this.product.nutrients, ...nutrientObject}
      });
      product.weights
          .filter(weight => weight.id !== GRAM_WEIGHT_ID)
          .forEach(weight => {
            this.product.weights[weight.id] = weight.weight;
          });
      Object.keys(this.product.months).forEach(month => {
        if (product.seasons.includes(month) === false) {
          this.product.months[month] = false;
          this.monthClicked(false, month);
        }
      });
      this.product.tags = product.tags;

      const storedProduct = await this.save(false, false);
      if (storedProduct !== null) {
          this.reset();
          await this.editProduct(storedProduct.id);
      }
    },
    close() {
      this.dialog = false;
      this.reset();
    },
    reset() {
      this.step = 1;
      this.product = {
        name: '',
        category: null,
        nutrients: {},
        weights: {},
        months: {
          1: true,
          2: true,
          3: true,
          4: true,
          5: true,
          6: true,
          7: true,
          8: true,
          9: true,
          10: true,
          11: true,
          12: true,
        },
        is_system: false,
        tags: []
      };
      this.seasons = {
        fullYear: true,
        q1: true,
        q2: true,
        q3: true,
        q4: true,
      };
      this.editingProductId = null;
      this.$refs.step0Form.resetValidation();
      this.$refs.step1Form.resetValidation();
      this.$refs.step2Form.resetValidation();
      this.$refs.step3Form.resetValidation();
      this.$refs.step4Form.resetValidation();
      this.$refs.step5Form.resetValidation();
      this.$refs.step6Form.resetValidation();
    },
    async validateZeroStep() {
      if (this.$refs.step0Form !== undefined) {
        await this.$refs.step0Form.validate();
      }
    },
    async goToStep(step) {
      await this.validateZeroStep();
      if (step > this.step) {
        const isValid = this.$refs[`step${this.step}Form`].validate() && this.$refs.step0Form.validate();
        if (isValid === false) {
          return;
        }
      }
      if (step === 6) {
        this.stepsValidity.sixthStep = true;
      }
      this.step = step;
    },
    flatNestedCategories(nestedCategory, level = 1) {
      const finalCategories = [];
      if (level > 1) {
        const prefix = new Array(level * 3).fill('\xA0').join('');
        nestedCategory.name =`${prefix}${nestedCategory.name}`;
      }
      finalCategories.push(nestedCategory);
      if (nestedCategory.subcategories.length > 0) {
        nestedCategory.subcategories.forEach(subcategory => {
          finalCategories.push(...this.flatNestedCategories(subcategory, level + 1));
        })
      }

      return finalCategories;
    },
    getMonths() {
      return [
        {number: 1, name: trans('app.months.january')},
        {number: 4, name: trans('app.months.april')},
        {number: 7, name: trans('app.months.july')},
        {number: 10, name: trans('app.months.october')},
        {number: 2, name: trans('app.months.february')},
        {number: 5, name: trans('app.months.may')},
        {number: 8, name: trans('app.months.august')},
        {number: 11, name: trans('app.months.november')},
        {number: 3, name: trans('app.months.march')},
        {number: 6, name: trans('app.months.june')},
        {number: 9, name: trans('app.months.september')},
        {number: 12, name: trans('app.months.december')},
      ];
    },
    fullYearClicked(checked) {
      for (let month = 1; month <= 12; ++month) {
        this.product.months[month] = checked;
      }
      this.seasons.q1 = checked;
      this.seasons.q2 = checked;
      this.seasons.q3 = checked;
      this.seasons.q4 = checked;
    },
    quarterClicked(checked, quarter) {
      this.seasons.fullYear = this.seasons.q1 && this.seasons.q2 && this.seasons.q3 && this.seasons.q4;

      const firstMonth = 1 + ((quarter - 1) * 3);
      for (let i = firstMonth; i < firstMonth + 3; ++i) {
        this.product.months[i] = checked;
      }
    },
    monthClicked(checked, month) {
      const quarter = Math.ceil(month / 3);
      if ((this.product.months.hasOwnProperty(((quarter - 1) * 3) + 1)
          && this.product.months.hasOwnProperty(((quarter - 1) * 3) + 2)
          && this.product.months.hasOwnProperty(((quarter - 1) * 3) + 3)) === false) {
        this.seasons[`q${quarter}`] = false;
      } else {
        this.seasons[`q${quarter}`] = this.product.months[((quarter - 1) * 3) + 1]
            && this.product.months[((quarter - 1) * 3) + 2]
            && this.product.months[((quarter - 1) * 3) + 3];
      }

      this.seasons.fullYear = this.seasons.q1 && this.seasons.q2 && this.seasons.q3 && this.seasons.q4;
    },
    async save(saveAndAdd, displayToast = true) {
      await this.validateZeroStep();
      await this.$refs.step1Form.validate();
      if (this.stepsValidity.zeroStep === false || this.stepsValidity.firstStep === false) {
        return null;
      }

      if (!this.checkIfThereIsAnyMonth()) {
        this.seasonsLack = true;

        return null;
      }

      const nutrients = helpers.deepClone(this.product.nutrients);
      if (nutrients.hasOwnProperty(this.GLYCEMIC_INDEX_ID) === false && nutrients.hasOwnProperty(this.CARBO_ID)) {
        if (+nutrients[this.CARBO_ID] === 0) {
          nutrients[this.GLYCEMIC_INDEX_ID] = 0;
        }
      }
      nutrients[this.WW_ID] = this.ww;
      nutrients[this.WBT_ID] = this.wbt;
      nutrients[this.GLYCEMIC_LOAD_ID] = this.gl;

      const isWeightNumeric = nutrient => isNaN(parseFloat(nutrient.weight)) === false;

      const product = {
        name: this.product.name,
        category_id: this.product.category,
        months: Object.keys(this.product.months).filter(month => this.product.months[month]),
        nutrients: Object.keys(nutrients)
            .map(nutrientId => ({id: nutrientId, weight: nutrients[nutrientId]}))
            .filter(isWeightNumeric),
        weights: Object.keys(this.product.weights)
            .map(weightId => ({id: weightId, weight: this.product.weights[weightId]}))
            .filter(isWeightNumeric),
        is_own: !this.product.is_system,
        tags: this.product.tags
      };

      if (saveAndAdd) {
        this.buttons.saveAndProceed = true;
      } else {
        this.buttons.save.loading = true;
      }
      let success = true;

      let storedProduct = null;
      try {
        if (!this.isAdding()) {
          await axios.put(`/v2/products/${this.editingProductId}`, product);
          (!displayToast) || this.$toast.success(trans('app.actions.edited', {name: trans('app.product').toLowerCase()}));
        } else {
          const {data} = await axios.post('/v2/products', product);
          storedProduct = data.data;
          (!displayToast) || this.$toast.success(trans('app.actions.added', {name: trans('app.product').toLowerCase()}));
        }
      } catch (e) {
        success = false;
        this.$toast.error(trans('app.error_occurred'));
        return null;
      }

      if (success) {
        await this.fetchOwnProductsCount();
        await this.FETCH_DICTIONARIES();
        this.$emit('productCreated');
      }

      if (saveAndAdd) {
        this.buttons.saveAndProceed = false;
      } else {
        this.buttons.save.loading = false;
      }

      if (saveAndAdd) {
        this.reset();
      } else {
        this.close();
      }

      return storedProduct;
    },
    checkIfThereIsAnyMonth() {
      return Object.keys(this.product.months).filter(key => this.product.months[key]).length > 0;
    },
    handleCarboChange() {
      this.switchCommaToPeriod(CARBO_ID);
      if (this.product.nutrients.hasOwnProperty(FIBER_ID) === false) {
        return;
      }
      if (+this.product.nutrients[FIBER_ID] <= 0) {
        return;
      }
      const fiberIndex = this.initialData[1].findIndex(nutrient => nutrient.id === FIBER_ID);
      if (fiberIndex !== -1) {
        this.$refs.step1Form.$children[fiberIndex].validate();
      }
    },
    switchCommaToPeriod(nutrientId) {
      const nutrientValue = String(this.product.nutrients[nutrientId]);
      if (nutrientValue.includes(',')) {
        this.product.nutrients[nutrientId] = nutrientValue.replace(',', '.');
      }
    },
    getTags(type) {
      return this.product.tags && this.product.tags.length > 0 ? this.product.tags.filter(t => t.type === type) : [];
    },
    setTags(tags, type) {
      tags.forEach((value, index) => {
        if (typeof value === 'string')
          tags[index] = {
            name: value,
            type,
            id: null,
          };
      });

      this.product.tags = this.product.tags.filter(t => t.type !== type);
      this.product.tags.push(...tags);
    },
    canSave() {
        if (!this.isAdding()) {
            return true;
        }
        return this.canCreate();
    },
    canCreate() {
      if (!this.hasPermission('product_own_create')) {
          return false;
      }
      return !((this.userRole === DIETITIAN_FREE || this.userRole === DIETITIAN_TEST) && this.ownProductsCount >= 20);
    },
    isAdding() {
      return this.editingProductId === null;
    }
  },
  computed: {
    ...mapState(['dictionaries']),
    rules: function () {
      return {
        nutrient: [
            ...this.rulesBuilder
                .addIsNumericRule('')
                .addNumberBiggerThanRule('', 0)
                .getRules()
        ],
        requiredNutrient: [
          ...this.rulesBuilder
              .addRequiredRule('')
              .addIsNumericRule('')
              .addNumberBiggerThanRule('', 0)
              .getRules(),
        ],
        fiber: [
          ...this.rulesBuilder
              .addIsNumericRule('')
              .addNumberBiggerThanRule('', 0)
              .getRules(),
          v => {
          const carboValue = +(this.product.nutrients.hasOwnProperty(CARBO_ID)
            ? this.product.nutrients[CARBO_ID]
              : 0);
            return !v || v <= carboValue || trans('app.products.create_modal.fiber_must_be_smaller_than_carbo');
          },
        ],
        name: [
            ...this.rulesBuilder
                .addRequiredRule(trans('app.name'))
                .addMinLengthRule(trans('app.name'), 1)
                .addMaxLengthRule(trans('app.name'), 255)
                .getRules()
        ],
        category: [
          ...this.rulesBuilder
              .addRequiredRule(trans('app.pages.products.category').toLowerCase())
              .getRules()
        ],
      }
    },
    ww() {
      const carbo = this.product.nutrients.hasOwnProperty(CARBO_ID) ? this.product.nutrients[CARBO_ID] : 0;
      const fiber = this.product.nutrients.hasOwnProperty(FIBER_ID) ? this.product.nutrients[FIBER_ID] : 0;

      return wwCalculation(carbo, fiber);
    },
    wbt() {
      const protein = this.product.nutrients.hasOwnProperty(PROTEIN_ID) ? this.product.nutrients[PROTEIN_ID] : 0;
      const fats = this.product.nutrients.hasOwnProperty(FATS_ID) ? this.product.nutrients[FATS_ID] : 0;

      return wbtCalculation(protein, fats);
    },
    gl() {
      const carbo = this.product.nutrients.hasOwnProperty(CARBO_ID) ? this.product.nutrients[CARBO_ID] : 0;
      const fiber = this.product.nutrients.hasOwnProperty(FIBER_ID) ? this.product.nutrients[FIBER_ID] : 0;
      const gi = this.product.nutrients.hasOwnProperty(this.GLYCEMIC_INDEX_ID) ? this.product.nutrients[this.GLYCEMIC_INDEX_ID] : 0;

      return glCalculation(carbo, fiber, gi);
    },
    ...mapGetters(['hasPermission', 'userRole', 'ownProductsCount']),
    currentStepValidity() {
      switch (this.step) {
        case 1:
          return this.stepsValidity.firstStep;
        case 2:
          return this.stepsValidity.secondStep;
        case 3:
          return this.stepsValidity.thirdStep;
        case 4:
          return this.stepsValidity.fourthStep;
        case 5:
          return this.stepsValidity.fifthStep;
        case 6:
          return this.stepsValidity.sixthStep;
      }

      return false;
    },
    saveButtonsDisabled() {
      return this.stepsValidity.zeroStep === false
          || this.seasonsLack === true
          || this.stepsValidity.firstStep === false;
    },
    categories() {
      let categories = helpers.deepClone(this.dictionaries.product_categories)
          .sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
      return categories.reduce((prevValue, currValue) => {
        return [...prevValue, ...this.flatNestedCategories(currValue)];
      }, []);
    },
    modalTitle() {
      if (this.isAdding()) {
        return trans('app.add_product');
      }

      return trans('app.edit_product');
    },
    productsTags: {
      get() {
        return this.getTags('product');
      },
      set(tags) {
        this.setTags(tags, 'product');
      },
    },
    productsItems() {
      return [...this.dictionaries.products, ...this.productsTags];
    },
  },
  watch: {
    'product.months': {
      handler() {
        this.seasonsLack = !this.checkIfThereIsAnyMonth();
      },
      deep: true,
    },
  },
}
</script>

<style scoped>
  .cursor-pointer {
    cursor: pointer;
  }

  .isMain {
    font-weight: bold;
  }

  .v-stepper__header{
    height:50px;
  }
  .v-stepper__step{
    padding:13px;
  }
  .v-stepper{
    height:430px;
  }
  ::v-deep .v-list__tile{
    height:32px;
  }

  ::v-deep .v-list__tile p{
    margin-bottom:0;
  }

  .v-dialog__content {
      z-index: 1030 !important;
  }
</style>
